import { useCallback } from 'react';
import {
  ContentItemCategory,
  type ContentItem,
} from '@/api/v4/content-items.api';
import { useFeedContentItems } from './use-feed-content-items.query';
import { VirtualGrid } from '../../components/virtual-grid/virtual-grid';
import { FeedCard } from './feed-card';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useTranslation } from 'react-i18next';
import { SmallText } from '@/ui/typography/action-panel';
import { useSendLearnMoreInquiry } from './use-send-learn-more-inquiry.mutation';
import { useGoToProjectFromFeed } from './use-go-to-project-from-feed.mutation';
import { openNewTab } from '@/helpers/open-new-tab';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

interface Props {
  category?: ContentItemCategory;
}

export const FeedBoard = ({ category }: Props) => {
  const { t } = useTranslation('feed');

  const { data = [] } = useFeedContentItems({
    category,
  });

  const { hasRequiredPermission } = useUserPermissions();
  const { isPending, sendInquiry } = useSendLearnMoreInquiry();
  const { goToProject, isPending: isGoToProjectLoading } =
    useGoToProjectFromFeed();

  const onLearnMore = useCallback(
    (item: ContentItem) => {
      if (item.category === ContentItemCategory.businessOpportunity) {
        void sendInquiry(item.id);
      } else {
        openNewTab(item.contentUrl);
      }
    },
    [sendInquiry],
  );

  const isCreateProjectVisible = (item: ContentItem) =>
    hasRequiredPermission(PERMISSION.ADD_PROJECT) &&
    item.category !== ContentItemCategory.businessOpportunity &&
    item.category !== ContentItemCategory.materials;

  const isGoToProjectVisible = (item: ContentItem) =>
    item.category === ContentItemCategory.materials;

  if (data.length === 0) {
    return (
      <Flexbox justify="center" height="100%" flex="1" alignItems="center">
        <SmallText>{t`noContent`}</SmallText>
      </Flexbox>
    );
  }

  return (
    <VirtualGrid<ContentItem>
      items={data}
      renderItem={item => (
        <FeedCard
          feed={item}
          showCreateProjectButton={isCreateProjectVisible(item)}
          showGoToProjectButton={isGoToProjectVisible(item)}
          handleGoToProject={() => goToProject(item.id)}
          handleLearnMore={() => onLearnMore(item)}
          isGoToProjectLoading={isGoToProjectLoading}
          isLoading={isPending}
        />
      )}
      estimateItemHeight={420}
      gap={{ x: 16, y: 16 }}
      initialLimit={8}
      incrementBy={4}
    />
  );
};
